import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useForm } from "@formspree/react";
import { STATES, TRAVELLERS } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FORMSPREE_ID = "mqengrao";

const initialForm = {
    name: "",
    email: "",
    phone: "",
    states: [],
    traveller_type: "",
    start_date: "",
    duration_days: "",
    group_size: "",
    budget: "",
    message: "",
};

export const PlanTrip = () => {
    const [form, setForm] = useState(initialForm);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [, submitToFormspree] = useForm(FORMSPREE_ID);

    // Honour pre-fill from journey detail page
    useEffect(() => {
        try {
            const raw = sessionStorage.getItem("enei_prefill");
            if (raw) {
                const data = JSON.parse(raw);
                setForm((f) => ({ ...f, ...data }));
                sessionStorage.removeItem("enei_prefill");
                setTimeout(() => {
                    document.getElementById("plan")?.scrollIntoView({ behavior: "smooth" });
                }, 200);
            }
        } catch (e) {
            // noop
        }
    }, []);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const toggleState = (slug) =>
        setForm((f) => ({
            ...f,
            states: f.states.includes(slug)
                ? f.states.filter((s) => s !== slug)
                : [...f.states, slug],
        }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.traveller_type) {
            toast.error("Tell us your name, email, and what kind of traveller you are.");
            return;
        }
        setLoading(true);
        try {
            const payload = {
                ...form,
                duration_days: form.duration_days ? Number(form.duration_days) : null,
                group_size: form.group_size ? Number(form.group_size) : null,
                start_date: form.start_date || null,
                budget: form.budget || null,
                message: form.message || null,
                phone: form.phone || null,
            };
            // Fire backend (MongoDB + email) and Formspree in parallel
            await Promise.allSettled([
                axios.post(`${API}/trip-plans`, payload),
                submitToFormspree({
                    _subject: `New Trip Plan — ${form.name}`,
                    name: form.name,
                    email: form.email,
                    phone: form.phone || "",
                    states: form.states.join(", "),
                    traveller_type: form.traveller_type,
                    start_date: form.start_date || "",
                    duration_days: form.duration_days || "",
                    group_size: form.group_size || "",
                    budget: form.budget || "",
                    message: form.message || "",
                }),
            ]);
            setDone(true);
            toast.success("We've got your note. A real person will write back soon.");
            setForm(initialForm);
        } catch (err) {
            console.error(err);
            const msg =
                err?.response?.data?.detail?.[0]?.msg ||
                err?.response?.data?.detail ||
                "Something went sideways. Try again?";
            toast.error(typeof msg === "string" ? msg : "Please check your details and try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="plan"
            data-testid="plan-trip-section"
            className="relative bg-stone py-16 md:py-32 lg:py-40 overflow-x-hidden"
        >
            <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 lg:px-14 flex flex-col lg:grid lg:grid-cols-12 gap-10 lg:gap-16 w-full">
                {/* Left: description */}
                <div className="w-full lg:col-span-5 reveal">
                    <p className="text-xs uppercase tracking-[0.28em] text-terracotta mb-4 md:mb-6">
                        — Plan my trip
                    </p>
                    <h2 className="font-serif text-forest text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tight font-light">
                        Write to us like you'd write to a friend who{" "}
                        <em className="italic text-terracotta">knows the road</em>.
                    </h2>
                    <p className="mt-6 md:mt-8 text-slatewarm text-sm sm:text-base md:text-[17px] leading-relaxed">
                        No call centres. No copy-paste itineraries. A real human reads every note,
                        usually with chai in hand, and writes back within two days. Tell us what
                        you're hoping for — even if it's only a feeling.
                    </p>

                    <ul className="mt-8 md:mt-12 space-y-3 md:space-y-4 text-sm text-slatewarm">
                        <li className="flex gap-3"><span className="text-terracotta shrink-0">✦</span> Permits &amp; paperwork handled where needed.</li>
                        <li className="flex gap-3"><span className="text-terracotta shrink-0">✦</span> Homestays first, hotels only when they earn it.</li>
                        <li className="flex gap-3"><span className="text-terracotta shrink-0">✦</span> Local guides who grew up on these trails.</li>
                    </ul>
                </div>

                {/* Right: form */}
                <div className="w-full lg:col-span-7 reveal">
                    {done ? (
                        <div
                            data-testid="plan-trip-success"
                            className="border border-forest/15 bg-paper p-10 md:p-16"
                        >
                            <p className="text-xs uppercase tracking-[0.28em] text-terracotta">
                                — Note received
                            </p>
                            <h3 className="mt-6 font-serif text-forest text-3xl md:text-5xl leading-tight font-light">
                                Thank you. We'll write back, by hand.
                            </h3>
                            <p className="mt-6 text-slatewarm leading-relaxed max-w-md">
                                Until then, the mountains are still where you left them.
                                Maybe go put the kettle on.
                            </p>
                            <button
                                onClick={() => setDone(false)}
                                data-testid="plan-trip-reset"
                                className="mt-10 text-xs uppercase tracking-[0.22em] text-forest border-b border-forest pb-1 hover:text-terracotta hover:border-terracotta transition-colors"
                            >
                                Send another note
                            </button>
                        </div>
                    ) : (
                        <form
                            onSubmit={submit}
                            data-testid="plan-trip-form"
                            className="border border-forest/15 bg-paper p-5 sm:p-8 md:p-12 space-y-8 md:space-y-10"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                                <Field label="Your name" testId="field-name">
                                    <input
                                        type="text"
                                        required
                                        data-testid="input-name"
                                        value={form.name}
                                        onChange={(e) => update("name", e.target.value)}
                                        className={inputCls}
                                        placeholder="e.g. Aditi Bordoloi"
                                    />
                                </Field>
                                <Field label="Email" testId="field-email">
                                    <input
                                        type="email"
                                        required
                                        data-testid="input-email"
                                        value={form.email}
                                        onChange={(e) => update("email", e.target.value)}
                                        className={inputCls}
                                        placeholder="you@somewhere.in"
                                    />
                                </Field>
                            </div>

                            <Field label="Mobile number (optional)" testId="field-phone">
                                <input
                                    type="tel"
                                    data-testid="input-phone"
                                    value={form.phone}
                                    onChange={(e) => update("phone", e.target.value)}
                                    className={inputCls}
                                    placeholder="+91 98765 43210"
                                />
                            </Field>

                            <Field label="Which states pull at you?" testId="field-states">
                                <div className="flex flex-wrap gap-2 pt-2">
                                    {STATES.map((s) => {
                                        const active = form.states.includes(s.slug);
                                        return (
                                            <button
                                                key={s.slug}
                                                type="button"
                                                data-testid={`chip-state-${s.slug}`}
                                                onClick={() => toggleState(s.slug)}
                                                className={`px-4 py-2 text-[12px] uppercase tracking-[0.18em] border transition-all ${
                                                    active
                                                        ? "bg-forest text-paper border-forest"
                                                        : "bg-transparent text-forest border-forest/30 hover:border-terracotta hover:text-terracotta"
                                                }`}
                                            >
                                                {s.name}
                                            </button>
                                        );
                                    })}
                                </div>
                            </Field>

                            <Field label="What kind of traveller are you, today?" testId="field-traveller">
                                <div className="grid sm:grid-cols-2 gap-3 pt-2">
                                    {TRAVELLERS.map((t) => {
                                        const active = form.traveller_type === t.id;
                                        return (
                                            <button
                                                key={t.id}
                                                type="button"
                                                data-testid={`radio-traveller-${t.id}`}
                                                onClick={() => update("traveller_type", t.id)}
                                                className={`text-left px-5 py-4 border transition-all ${
                                                    active
                                                        ? "bg-forest text-paper border-forest"
                                                        : "bg-transparent text-forest border-forest/30 hover:border-terracotta"
                                                }`}
                                            >
                                                <div className="font-serif italic text-marigold text-sm">
                                                    {t.number}
                                                </div>
                                                <div className="font-serif text-lg md:text-xl leading-tight">
                                                    {t.title.replace("The ", "")}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </Field>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8">
                                <Field label="Around when?" testId="field-date">
                                    <input
                                        type="date"
                                        data-testid="input-start-date"
                                        value={form.start_date}
                                        onChange={(e) => update("start_date", e.target.value)}
                                        className={inputCls}
                                    />
                                </Field>
                                <Field label="How many days?" testId="field-duration">
                                    <input
                                        type="number"
                                        min="1"
                                        max="120"
                                        data-testid="input-duration"
                                        value={form.duration_days}
                                        onChange={(e) => update("duration_days", e.target.value)}
                                        className={inputCls}
                                        placeholder="7"
                                    />
                                </Field>
                                <Field label="How many of you?" testId="field-group">
                                    <input
                                        type="number"
                                        min="1"
                                        max="50"
                                        data-testid="input-group-size"
                                        value={form.group_size}
                                        onChange={(e) => update("group_size", e.target.value)}
                                        className={inputCls}
                                        placeholder="2"
                                    />
                                </Field>
                            </div>

                            <Field label="A rough budget per person (optional)" testId="field-budget">
                                <select
                                    data-testid="input-budget"
                                    value={form.budget}
                                    onChange={(e) => update("budget", e.target.value)}
                                    className={`${inputCls} cursor-pointer`}
                                >
                                    <option value="">— pick a range —</option>
                                    <option value="under-30k">Under ₹30,000</option>
                                    <option value="30-60k">₹30,000 – ₹60,000</option>
                                    <option value="60-120k">₹60,000 – ₹1,20,000</option>
                                    <option value="120k+">₹1,20,000 +</option>
                                    <option value="open">Open / advise me</option>
                                </select>
                            </Field>

                            <Field label="Tell us what you're hoping for" testId="field-message">
                                <textarea
                                    rows={4}
                                    data-testid="input-message"
                                    value={form.message}
                                    onChange={(e) => update("message", e.target.value)}
                                    className={`${inputCls} resize-none pt-3`}
                                    placeholder="A festival, a trail, a feeling. Even one line is enough."
                                />
                            </Field>

                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="plan-trip-submit"
                                className="inline-flex items-center gap-3 bg-forest text-paper px-8 py-4 text-[12px] uppercase tracking-[0.22em] hover:bg-terracotta transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {loading ? "Sending…" : "Send my note"}
                                <span aria-hidden>→</span>
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
};

const inputCls =
    "w-full bg-transparent border-0 border-b border-forest/30 rounded-none focus:outline-none focus:border-terracotta focus:ring-0 py-2 text-forest placeholder:text-forest/30 text-base transition-colors";

const Field = ({ label, testId, children }) => (
    <label data-testid={testId} className="block">
        <span className="block text-[11px] uppercase tracking-[0.22em] text-slatewarm mb-1">
            {label}
        </span>
        {children}
    </label>
);
